import React, { useRef } from 'react';
import DOMPurify from 'dompurify';
import { Button, Image, Select, Space, Typography } from 'antd';
import { EditOutlined } from '@ant-design/icons';
import { routeHistory } from '@nackle/biw-web-router';
import { Routes } from '../../../routes';
import { MISSING_IMAGE } from '../../consts';
import { useFeatureFlags } from '../../../featureFlags';
import { flags } from '../../../featureFlags/flags';
import DeleteButton from '../../components/DeleteButton/DeleteButton';
import TagsList from '../../components/TagsList/TagsList';
import LinksList from '../../components/LinksList/LinksList';
import { renderWithSearchWords } from '../../components/SearchBar/SearchBar';
import useDeleteCurricula from '../../gql/hooks/useDeleteCurricula'; 
import SearchQueryList from '../../components/SearchQueryList/SearchQueryList';
import ListSearchItemsByIds from '../../components/ListSearchItemsByIds/ListSearchItemsByIds';
import { ColoredTag } from '../GenericCreateEdit/GenericElements';

let expandedRows = [];

const CurriculaListPage = () => { 
    const listPageRef = useRef( null );
    const [ showWeight ] = useFeatureFlags( [ flags.CURRICULA_WEIGHT_ENABLED ] );
    const [ showBrands ] = useFeatureFlags( [ flags.BRANDS ] );
    const { deleteCurricula, isDeleting } = useDeleteCurricula();
    
    const breadcrumbs = [
        { name: 'Course Studio', path: Routes.Home.buildPath() },
        { name: 'Learning Content', path: Routes.LearningContentNav.buildPath() },
        { name: 'Curricula' }
    ];

    const uiStrings = {
        cardTitle: 'Curricula',
        createButtonTitle: 'Create New Curricula'
    };

    const handleDeleteClick = async ( id ) => {
        await deleteCurricula( id, () => {
            listPageRef.current.reload();
        } );
    };

    const handleCreateClick = () => {
        routeHistory.push( Routes.CreateCurriculum.buildPath() );
    };

    const handleSetExpandedRows = ( rows ) => {
        expandedRows = rows;
    };

    const renderExpandedRow = ( curricula ) => {
        return (
            <div className="row-expand">
                <div className="flex-container">
                    <div className="flex-left">
                        <strong>Content</strong><br/>
    
                        { ( !curricula.content && !curricula.name ) && 
                            <p className="no-translation">No translation for this language</p>
                        }
                                                        
                        { ( curricula.content || curricula.name ) && 
                        <Typography.Paragraph ellipsis={ { rows: 6 } }>
                            <span dangerouslySetInnerHTML={ { __html: DOMPurify.sanitize( curricula.content ) } } /> 
                        </Typography.Paragraph>
                        }
                    </div>
    
                    <div className="flex-right">
                        <Select
                            bordered={ false }
                            dropdownMatchSelectWidth={ 200 }
                            defaultValue={ curricula._selectedLocale }
                            onChange={ ( value ) => listPageRef.current.handleLocaleChange( value, curricula ) }
                            options={ 
                                curricula.locales.map( l => (
                                    {
                                        key: l.code,
                                        value: l.code,
                                        label: listPageRef.current.getLabelForLocale( l.code )
                                    }
                                ) )
                            }
                            size="small"
                        />
                    </div>
                </div>    

                { /* Courses list */ }
                <LinksList 
                    title="Courses"
                    items={ curricula.courses } 
                    handleClickCallback= { ( id ) => routeHistory.push( Routes.EditCourse.buildPath( id ) ) } 
                />
 
                { /* Prerequisists of list */ }
                <ListSearchItemsByIds 
                    type={ 'curricula' }
                    ids={ curricula.prerequisiteOf }
                    title="Prerequisite Of"
                />
                
                { /* Tags list */ }
                <TagsList tags={ curricula.tags }/>       
            </div>
        );
    };

    const tableColumns = [
        {
            dataIndex: 'thumbnailImage',
            key: 'thumbnailImage', 
            title: 'Thumbnail',
            width: 120,
            fixed: 'left',
            render: ( thumbnailImage ) => 
                <Image 
                    width="100px"
                    className="thumbnail-image" 
                    fallback={ MISSING_IMAGE }
                    src={ thumbnailImage || MISSING_IMAGE }
                    preview={ !!thumbnailImage }
                />
        },
        {
            dataIndex: 'name',
            key: 'name', 
            title: 'Name',
            sorter: ( a, b ) => 0,
            sortKey: 'name',
            width: 350,
            fixed: 'left',
            render: ( name, i ) => {
                if ( !i.content && !i.name ) {
                    return <span className="no-translation">No translation for this language</span>;
                } else {
                    return (
                        <span className="name-column">
                            { renderWithSearchWords( name, listPageRef.current.getSearchWords() ) }
                        </span> 
                    );
                }
            }
        },
        {
            dataIndex: 'weight',
            title: 'Sort Order',
            key: 'weight',
            sorter: ( a, b ) => 0,
            sortKey: 'weight',
            hidden: !showWeight,
            render: ( weight ) => <span>{ weight }</span>
        },
        ...( showBrands ?
            [
                {
                    dataIndex: 'brandIds',
                    key: 'brands',
                    title: 'Brand',
                    sortKey: 'brands',
                    render: ( b ) => {
                        return b.map( i => {
                            const brand = listPageRef.current.getBrand( i );
                            return brand ? <ColoredTag.Brand key={ brand.id } brand={ brand } /> : null;
                        } );
                    }
                },
            ] : [] ),
        {
            dataIndex: 'createdAt',
            key: 'createdAt',
            title: 'Created At',
            sorter: ( a, b ) => 0,
            sortKey: 'createdAt',
            render: ( createdAt ) => <span>{ createdAt }</span>
        },
        {
            dataIndex: 'updatedAt',
            key: 'updatedAt',
            title: 'Updated At',
            sorter: ( a, b ) => 0,
            sortKey: 'updatedAt',
            render: ( updatedAt ) => <span>{ updatedAt }</span>
        },
        {
            title: 'Actions',
            key: 'actions', 
            fixed: 'right', 
            width: '100px',
            render: ( item ) => {
                return (
                    <>
                        <Space>
                            <Button 
                                type="link" 
                                icon={ <EditOutlined /> } 
                                onClick= { () => routeHistory.push( Routes.EditCurriculum.buildPath( item.id ) ) }
                            />
                            
                            <DeleteButton
                                id={ item.id }   
                                title={ 'Confirm Delete' }
                                disabled={ item.prerequisiteOf && item.prerequisiteOf.length > 0 }
                                deleteCallback={ ( i ) => handleDeleteClick( i ) }
                                confirmMessage= { 
                                    <>
                                        <p>Are you <i>sure</i> you want to permanently delete the curricula <b>{ item.name }</b>? 
                                        This action cannot be undone.</p>
                                    </>
                                }
                            />
                        </Space>
                    </> 
                );
            }   
        }
    ].filter( item => !item.hidden );
  
    return (
        <SearchQueryList 
            ref={ listPageRef }
            type="curricula" 
            uiStrings={ uiStrings }
            breadcrumbs={ breadcrumbs }
            tableColumns={ tableColumns }
            isDeleting={ isDeleting }
            renderExpandedRowCallback= { renderExpandedRow }
            createClickCallback={ handleCreateClick }
            setExpandedRowsCallback= { handleSetExpandedRows }
            defaultExpandedRows= { expandedRows }
        />
    );
};


export default CurriculaListPage;
